import { Link } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, Search, ChevronDown, ChevronRight, Sparkles, Tag, Plus, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNavMenu } from '../hooks/useNavMenu';
import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { cleanTitle } from './ProductCard';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';

/* ─── Mega-menu panel (desktop) ─── */
const MegaMenuPanel = ({ children }) => (
  <div
    className="absolute top-full left-0 mt-0 bg-white shadow-xl border border-[#E5D8D0]/60 z-50 min-w-[420px] animate-in fade-in slide-in-from-top-2 duration-200"
    data-testid="mega-menu-panel"
  >
    <div className="p-6">{children}</div>
  </div>
);

/* ─── Single nav trigger with hover mega-menu ─── */
const MegaMenuTrigger = ({ label, testId, compact, children }) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const enter = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  }, []);

  const leave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setOpen(false), 250);
  }, []);

  // Close on click outside (supports touch/trackpad devices)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  // Keyboard: Escape closes and returns focus
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const linkClass = `tracking-[0.12em] text-white/80 hover:text-[#DEA193] transition-colors whitespace-nowrap flex items-center gap-1 ${compact ? 'text-xs' : 'text-sm tracking-[0.15em]'}`;

  return (
    <div
      className="relative"
      ref={containerRef}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      <button
        ref={triggerRef}
        className={linkClass}
        data-testid={testId}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <MegaMenuPanel>
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </MegaMenuPanel>
      )}
    </div>
  );
};

/* ─── Category link inside mega-menu ─── */
const MegaCategoryLink = ({ to, name, count, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center justify-between py-1.5 text-sm text-[#333] hover:text-[#DEA193] transition-colors group"
  >
    <span className="group-hover:translate-x-0.5 transition-transform">{name}</span>
    <span className="text-[11px] text-[#999]">{count}</span>
  </Link>
);

/* ─── Brand link inside mega-menu ─── */
const MegaBrandLink = ({ to, name, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="block py-1.5 text-sm text-[#555] hover:text-[#DEA193] transition-colors"
  >
    {name}
  </Link>
);

/* ─── Quick-add product card inside mega-menu ─── */
const MegaQuickAdd = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [selectedSize, setSelectedSize] = useState('');
  const [added, setAdded] = useState(false);
  const hasSizes = product.sizes?.length > 0;
  const needsSize = hasSizes && !selectedSize;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (needsSize) return;
    const price = product.on_sale && product.sale_price ? product.sale_price : product.price;
    addItem({ id: product.id, name: product.name, price, image: product.image, size: selectedSize || 'One Size' });
    setAdded(true);
    toast.success(`${product.name} added to bag`);
    setTimeout(() => { setAdded(false); onClose?.(); }, 1200);
  };

  return (
    <div className="flex items-center gap-3 py-2 group/qa" data-testid={`mega-quick-add-${product.id}`}>
      <Link to={`/product/${product.id}`} onClick={onClose} className="flex-shrink-0">
        <div className="w-14 h-14 bg-[#F5F5F5] overflow-hidden">
          {product.image ? (
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#F0EFE7]">
              <svg className="w-4 h-4 text-[#C4B5A8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
              </svg>
            </div>
          )}
        </div>
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/product/${product.id}`} onClick={onClose} className="block text-xs text-[#333] hover:text-[#DEA193] truncate font-medium">
          {cleanTitle(product.name, product.brand, product.colors)}
        </Link>
        <div className="flex items-center gap-1.5 mt-0.5">
          {product.on_sale && product.sale_price ? (
            <>
              <span className="text-xs text-[#DEA193] font-medium">£{product.sale_price.toFixed(2)}</span>
              <span className="text-[10px] text-[#999] line-through">£{product.price.toFixed(2)}</span>
            </>
          ) : (
            <span className="text-xs text-[#555]">£{product.price.toFixed(2)}</span>
          )}
        </div>
        {hasSizes && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {product.sizes.slice(0, 4).map(s => (
              <button
                key={s}
                onClick={(e) => { e.stopPropagation(); setSelectedSize(s); }}
                className={`px-1.5 py-0.5 text-[10px] border transition-colors ${selectedSize === s ? 'border-[#DEA193] text-[#DEA193] bg-[#FEF6F4]' : 'border-[#E5D8D0] text-[#777] hover:border-[#DEA193]'}`}
                data-testid={`size-option-${s}`}
              >
                {s}
              </button>
            ))}
            {product.sizes.length > 4 && <span className="text-[10px] text-[#999] self-center">+{product.sizes.length - 4}</span>}
          </div>
        )}
      </div>
      <button
        onClick={handleAdd}
        disabled={needsSize || added}
        className={`flex-shrink-0 p-1.5 transition-all ${added ? 'bg-green-500 text-white' : needsSize ? 'bg-[#F0F0F0] text-[#999] cursor-not-allowed' : 'bg-[#111] text-white hover:bg-[#DEA193]'}`}
        aria-label={added ? `${product.name} added` : needsSize ? `Select a size for ${product.name}` : `Add ${product.name} to bag`}
        data-testid={`quick-add-btn-${product.id}`}
      >
        {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

/* ─── Desktop navigation ─── */
export const DesktopNav = ({ compact = false }) => {
  const { nav, loading } = useNavMenu();
  const [brandsOpen, setBrandsOpen] = useState(false);
  const brandsRef = useRef(null);
  const brandsTimeout = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (brandsRef.current && !brandsRef.current.contains(e.target)) setBrandsOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const linkClass = `tracking-[0.12em] text-white/80 hover:text-[#DEA193] transition-colors whitespace-nowrap ${compact ? 'text-xs' : 'text-sm tracking-[0.15em]'}`;

  if (loading || !nav) return <nav className={`hidden md:flex items-center ${compact ? 'space-x-4' : 'space-x-6'}`} />;

  const hasDance = nav.dance?.categories?.length > 0;
  const hasActive = nav.activewear?.categories?.length > 0;
  const hasAccessories = nav.accessories?.count > 0;
  const hasNutrition = nav.nutrition?.count > 0;
  const hasBrands = nav.brands?.length > 0;
  const hasSale = nav.sale_count > 0;

  return (
    <nav className={`hidden md:flex items-center ${compact ? 'space-x-4' : 'space-x-6'}`} data-testid="desktop-nav">
      {/* DANCE mega-menu */}
      {hasDance && (
        <MegaMenuTrigger label="DANCE" testId="nav-dance-trigger" compact={compact}>
          {(close) => (
            <div className="grid grid-cols-3 gap-6 min-w-[560px]">
              <div>
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-3 font-medium">Shop by Category</h3>
                {nav.dance.categories.map(c => (
                  <MegaCategoryLink key={c.slug} to={`/shop?category=${encodeURIComponent(c.slug)}&department=Dance`} name={c.name} count={c.count} onClick={close} />
                ))}
                <Link
                  to="/shop?department=Dance"
                  onClick={close}
                  className="inline-flex items-center gap-1 mt-4 text-xs uppercase tracking-[0.15em] text-[#DEA193] hover:text-[#c4897e] transition-colors font-medium"
                  data-testid="nav-dance-shop-all"
                >
                  Shop All Dance <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              {nav.dance.brands?.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-3 font-medium">Dance Brands</h3>
                  {nav.dance.brands.map(b => (
                    <MegaBrandLink key={b.name} to={`/brand/${encodeURIComponent(b.name)}`} name={b.name} onClick={close} />
                  ))}
                </div>
              )}
              {nav.dance.featured?.length > 0 && (
                <div className="border-l border-[#E5D8D0]/60 pl-6">
                  <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-2 font-medium">Quick Add</h3>
                  {nav.dance.featured.map(p => (
                    <MegaQuickAdd key={p.id} product={p} onClose={close} />
                  ))}
                </div>
              )}
            </div>
          )}
        </MegaMenuTrigger>
      )}

      {/* ACTIVEWEAR mega-menu */}
      {hasActive && (
        <MegaMenuTrigger label="ACTIVEWEAR" testId="nav-activewear-trigger" compact={compact}>
          {(close) => (
            <div className="grid grid-cols-3 gap-6 min-w-[560px]">
              <div>
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-3 font-medium">Shop by Category</h3>
                {nav.activewear.categories.map(c => (
                  <MegaCategoryLink key={c.slug} to={`/shop?category=${encodeURIComponent(c.slug)}&department=Activewear`} name={c.name} count={c.count} onClick={close} />
                ))}
                <Link
                  to="/shop?department=Activewear"
                  onClick={close}
                  className="inline-flex items-center gap-1 mt-4 text-xs uppercase tracking-[0.15em] text-[#DEA193] hover:text-[#c4897e] transition-colors font-medium"
                  data-testid="nav-activewear-shop-all"
                >
                  Shop All Activewear <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              {nav.activewear.brands?.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-3 font-medium">Active Brands</h3>
                  {nav.activewear.brands.map(b => (
                    <MegaBrandLink key={b.name} to={`/brand/${encodeURIComponent(b.name)}`} name={b.name} onClick={close} />
                  ))}
                </div>
              )}
              {nav.activewear.featured?.length > 0 && (
                <div className="border-l border-[#E5D8D0]/60 pl-6">
                  <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-2 font-medium">Quick Add</h3>
                  {nav.activewear.featured.map(p => (
                    <MegaQuickAdd key={p.id} product={p} onClose={close} />
                  ))}
                </div>
              )}
            </div>
          )}
        </MegaMenuTrigger>
      )}

      {/* ACCESSORIES — direct link to collection */}
      {hasAccessories && (
        <Link
          to="/shop?department=Accessories"
          className={linkClass}
          data-testid="nav-accessories"
        >
          ACCESSORIES
        </Link>
      )}

      {/* NUTRITION — direct link */}
      {hasNutrition && (
        <Link to="/shop?department=Nutrition" className={linkClass} data-testid="nav-nutrition">
          NUTRITION
        </Link>
      )}

      {/* BRANDS — hover dropdown */}
      {hasBrands && (
        <div
          className="relative"
          ref={brandsRef}
          onMouseEnter={() => { clearTimeout(brandsTimeout.current); setBrandsOpen(true); }}
          onMouseLeave={() => { brandsTimeout.current = setTimeout(() => setBrandsOpen(false), 200); }}
        >
          <button
            onClick={() => setBrandsOpen(o => !o)}
            className={`${linkClass} flex items-center gap-1`}
            data-testid="nav-brands-trigger"
            aria-expanded={brandsOpen}
            aria-haspopup="true"
          >
            BRANDS <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${brandsOpen ? 'rotate-180' : ''}`} />
          </button>
          {brandsOpen && (
            <div
              className="absolute top-full left-0 mt-0 min-w-[200px] bg-white shadow-xl border border-[#E5D8D0]/60 z-50 py-2 max-h-80 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200"
              data-testid="nav-brands-dropdown"
            >
              {nav.brands.map(b => (
                <Link
                  key={b.id}
                  to={`/brand/${encodeURIComponent(b.id)}`}
                  className="flex items-center justify-between px-4 py-2 text-sm text-[#333] hover:bg-[#F7F7EB] hover:text-[#DEA193] transition-colors"
                  onClick={() => setBrandsOpen(false)}
                  data-testid={`nav-brand-${b.id.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="flex items-center gap-2">
                    {b.logo_url && <img src={b.logo_url} alt="" className="h-4 w-auto object-contain" />}
                    {b.name}
                  </span>
                  <span className="text-[11px] text-[#999]">{b.count}</span>
                </Link>
              ))}
              <div className="border-t border-[#E5D8D0] mt-1 pt-1">
                <Link
                  to="/shop"
                  className="block px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#DEA193] hover:bg-[#F7F7EB] transition-colors"
                  onClick={() => setBrandsOpen(false)}
                  data-testid="nav-shop-all-brands"
                >
                  Shop All Brands
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NEW — always present, links to newest sort */}
      <Link to="/shop?sort=newest" className={`${linkClass} flex items-center gap-1`} data-testid="nav-new">
        <Sparkles className="w-3 h-3" />
        NEW
      </Link>

      {/* SALE — only if sale items exist */}
      {hasSale && (
        <Link
          to="/shop?on_sale=true"
          className={`tracking-[0.12em] text-[#DEA193] hover:text-white transition-colors whitespace-nowrap flex items-center gap-1 font-medium ${compact ? 'text-xs' : 'text-sm tracking-[0.15em]'}`}
          data-testid="nav-sale"
        >
          <Tag className="w-3 h-3" />
          SALE
        </Link>
      )}
    </nav>
  );
};

/* ─── Mobile accordion section ─── */
const MobileAccordion = ({ title, testId, children, onClose }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-[#222]">
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex items-center justify-between w-full py-3 text-sm uppercase tracking-[0.12em] text-white/80 hover:text-[#DEA193] transition-colors"
        data-testid={testId}
        aria-expanded={expanded}
      >
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${expanded ? 'max-h-[500px] opacity-100 pb-3' : 'max-h-0 opacity-0'}`}
      >
        <div className="pl-4 space-y-0.5 border-l border-[#333]">
          {typeof children === 'function' ? children(onClose) : children}
        </div>
      </div>
    </div>
  );
};

const MobileNavLink = ({ to, label, onClick, accent }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`block text-sm py-2 transition-colors ${accent ? 'text-[#DEA193] hover:text-white font-medium' : 'text-white/60 hover:text-[#DEA193]'}`}
  >
    {label}
  </Link>
);

/* ─── Mobile navigation ─── */
export const MobileMenu = ({ open, onClose }) => {
  const { nav, loading } = useNavMenu();
  const menuRef = useRef(null);

  // Focus trap + Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !menuRef.current) return;
      const focusable = menuRef.current.querySelectorAll('a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const hasDance = nav?.dance?.categories?.length > 0;
  const hasActive = nav?.activewear?.categories?.length > 0;
  const hasAccessories = nav?.accessories?.count > 0;
  const hasNutrition = nav?.nutrition?.count > 0;
  const hasBrands = nav?.brands?.length > 0;
  const hasSale = nav?.sale_count > 0;

  return (
    <div ref={menuRef} id="mobile-menu-panel" className="md:hidden py-2 border-t border-[#222222] animate-in slide-in-from-top-1 duration-200" data-testid="mobile-menu" role="dialog" aria-label="Main menu">
      <nav className="flex flex-col" aria-label="Mobile navigation">
        {/* Shop All */}
        <div className="border-b border-[#222]">
          <Link to="/shop" onClick={onClose} className="block py-3 text-sm uppercase tracking-[0.12em] text-white/80 hover:text-[#DEA193]" data-testid="mobile-nav-shop">
            Shop All
          </Link>
        </div>

        {/* DANCE accordion */}
        {hasDance && (
          <MobileAccordion title="Dance" testId="mobile-nav-dance-toggle" onClose={onClose}>
            {(close) => (
              <>
                {nav.dance.categories.map(c => (
                  <MobileNavLink key={c.slug} to={`/shop?category=${encodeURIComponent(c.slug)}&department=Dance`} label={`${c.name} (${c.count})`} onClick={close} />
                ))}
                <MobileNavLink to="/shop?department=Dance" label="Shop All Dance" onClick={close} accent />
                {nav.dance.brands?.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-3 mb-1 font-medium">Dance Brands</div>
                    {nav.dance.brands.map(b => (
                      <MobileNavLink key={b.name} to={`/brand/${encodeURIComponent(b.name)}`} label={b.name} onClick={close} />
                    ))}
                  </>
                )}
              </>
            )}
          </MobileAccordion>
        )}

        {/* ACTIVEWEAR accordion */}
        {hasActive && (
          <MobileAccordion title="Activewear" testId="mobile-nav-activewear-toggle" onClose={onClose}>
            {(close) => (
              <>
                {nav.activewear.categories.map(c => (
                  <MobileNavLink key={c.slug} to={`/shop?category=${encodeURIComponent(c.slug)}&department=Activewear`} label={`${c.name} (${c.count})`} onClick={close} />
                ))}
                <MobileNavLink to="/shop?department=Activewear" label="Shop All Activewear" onClick={close} accent />
                {nav.activewear.brands?.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-3 mb-1 font-medium">Active Brands</div>
                    {nav.activewear.brands.map(b => (
                      <MobileNavLink key={b.name} to={`/brand/${encodeURIComponent(b.name)}`} label={b.name} onClick={close} />
                    ))}
                  </>
                )}
              </>
            )}
          </MobileAccordion>
        )}

        {/* ACCESSORIES accordion */}
        {hasAccessories && (
          <MobileAccordion title="Accessories" testId="mobile-nav-accessories-toggle" onClose={onClose}>
            {(close) => (
              <>
                {nav.accessories.categories.map(c => (
                  <MobileNavLink key={c.slug} to={`/shop?category=${encodeURIComponent(c.slug)}&department=Accessories`} label={`${c.name} (${c.count})`} onClick={close} />
                ))}
                <MobileNavLink to="/shop?department=Accessories" label="Shop All Accessories" onClick={close} accent />
              </>
            )}
          </MobileAccordion>
        )}

        {/* NUTRITION — direct link */}
        {hasNutrition && (
          <div className="border-b border-[#222]">
            <Link to="/shop?department=Nutrition" onClick={onClose} className="block py-3 text-sm uppercase tracking-[0.12em] text-white/80 hover:text-[#DEA193]" data-testid="mobile-nav-nutrition">
              Nutrition
            </Link>
          </div>
        )}

        {/* BRANDS accordion */}
        {hasBrands && (
          <MobileAccordion title="Brands" testId="mobile-nav-brands-toggle" onClose={onClose}>
            {(close) => (
              <>
                {nav.brands.map(b => (
                  <MobileNavLink key={b.id} to={`/brand/${encodeURIComponent(b.id)}`} label={`${b.name} (${b.count})`} onClick={close} />
                ))}
                <MobileNavLink to="/shop" label="Shop All Brands" onClick={close} accent />
              </>
            )}
          </MobileAccordion>
        )}

        {/* NEW */}
        <div className="border-b border-[#222]">
          <Link to="/shop?sort=newest" onClick={onClose} className="flex items-center gap-2 py-3 text-sm uppercase tracking-[0.12em] text-white/80 hover:text-[#DEA193]" data-testid="mobile-nav-new">
            <Sparkles className="w-3.5 h-3.5" />
            New In
          </Link>
        </div>

        {/* SALE */}
        {hasSale && (
          <div className="border-b border-[#222]">
            <Link to="/shop?on_sale=true" onClick={onClose} className="flex items-center gap-2 py-3 text-sm uppercase tracking-[0.12em] text-[#DEA193] hover:text-white font-medium" data-testid="mobile-nav-sale">
              <Tag className="w-3.5 h-3.5" />
              Sale
            </Link>
          </div>
        )}
      </nav>
    </div>
  );
};

/* ─── Header actions (search, cart, user, mobile toggle) ─── */
export const HeaderActions = ({ compact = false, searchOpen, setSearchOpen, mobileMenuOpen, setMobileMenuOpen }) => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const iconSize = compact ? 'w-4 h-4' : 'w-5 h-5';
  const btnPad = compact ? 'p-1.5' : 'p-2';

  return (
    <div className={`flex items-center ${compact ? 'space-x-3' : 'space-x-4'}`}>
      <button onClick={() => setSearchOpen(!searchOpen)} className={`${btnPad} hover:bg-white/10 rounded-sm text-white`} data-testid="search-toggle" aria-label="Search">
        <Search className={iconSize} />
      </button>
      <Link to="/cart" className={`relative ${btnPad} hover:bg-white/10 rounded-sm text-white`} data-testid="cart-link" aria-label={`Cart${itemCount > 0 ? ` (${itemCount} items)` : ''}`}>
        <ShoppingBag className={iconSize} />
        {itemCount > 0 && (
          <span className={`absolute -top-1 -right-1 ${compact ? 'w-4 h-4 text-[10px]' : 'w-5 h-5 text-xs'} bg-[#DEA193] text-white rounded-full flex items-center justify-center`} data-testid="cart-count" aria-hidden="true">
            {itemCount}
          </span>
        )}
      </Link>
      {isAuthenticated ? (
        <UserDropdown user={user} isAdmin={isAdmin} logout={logout} compact={compact} />
      ) : (
        <Link to="/login" className={`${btnPad} hover:bg-white/10 rounded-sm text-white`} data-testid="login-link" aria-label="Sign in">
          <User className={iconSize} />
        </Link>
      )}
      <button className={`md:hidden ${btnPad} hover:bg-white/10 rounded-sm text-white`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} aria-controls="mobile-menu-panel">
        {mobileMenuOpen ? <X className={iconSize} aria-hidden="true" /> : <Menu className={iconSize} aria-hidden="true" />}
      </button>
    </div>
  );
};

const UserDropdown = ({ user, isAdmin, logout, compact }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className={`${compact ? 'p-1.5' : 'p-2'} hover:bg-white/10 rounded-sm text-white`} data-testid="user-menu-trigger" aria-label="Account menu">
        <User className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48 bg-white rounded-sm">
      <div className="px-3 py-2 border-b border-[#E5D8D0]">
        <p className="text-sm font-medium">{user?.name || 'User'}</p>
        <p className="text-xs text-[#737373]">{user?.email}</p>
      </div>
      <DropdownMenuItem asChild><Link to="/account/orders" className="cursor-pointer" data-testid="menu-orders">My Orders</Link></DropdownMenuItem>
      <DropdownMenuItem asChild><Link to="/wishlist" className="cursor-pointer" data-testid="menu-wishlist">Wishlist</Link></DropdownMenuItem>
      <DropdownMenuItem asChild><Link to="/account/settings" className="cursor-pointer" data-testid="menu-settings">Account Settings</Link></DropdownMenuItem>
      {isAdmin && (<><DropdownMenuSeparator /><DropdownMenuItem asChild><Link to="/admin" className="cursor-pointer text-[#DEA193]" data-testid="menu-admin">Admin Dashboard</Link></DropdownMenuItem></>)}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600" data-testid="menu-logout">Logout</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const SearchBar = ({ query, setQuery, onSubmit, compact = false }) => (
  <div className={`${compact ? 'py-2' : 'py-4'} border-t border-[#222222]`}>
    <form onSubmit={onSubmit} className="flex items-center gap-2">
      <input
        type="text" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Search for products..." autoFocus
        className={`flex-1 ${compact ? 'px-3 py-2 text-sm' : 'px-4 py-3'} border border-[#E5D8D0] rounded-sm focus:outline-none focus:border-[#DEA193] bg-white text-[#000000]`}
        data-testid="search-input"
      />
      <button type="submit" className={`${compact ? 'px-4 py-2 bg-[#DEA193] text-white text-sm' : 'btn-primary'}`} data-testid="search-submit">Search</button>
    </form>
  </div>
);
